// Ошибки JS публичного сайта в Sentry. Отдельный бандл: грузится только в production
// (layouts/_sentry), сайт без него работает так же. Клиент собирается руками, а не через
// `Sentry.init`: тот тянет все интеграции по умолчанию, и бандл вырос бы вдвое.
import {
  BrowserClient,
  breadcrumbsIntegration,
  browserApiErrorsIntegration,
  dedupeIntegration,
  defaultStackParser,
  eventFiltersIntegration,
  functionToStringIntegration,
  getCurrentScope,
  globalHandlersIntegration,
  httpContextIntegration,
  isBotUserAgent,
  linkedErrorsIntegration,
  makeFetchTransport
} from "@sentry/browser"

// Квота ошибок — 5000 в месяц на весь проект: зациклившийся обработчик у одного посетителя не
// должен её съесть.
const EVENTS_PER_PAGE_LOAD = 5
// Реплеев 50 в месяц: один браузер с повторяющейся ошибкой забрал бы их за вечер.
const REPLAY_EVERY_MS = 24 * 60 * 60 * 1000
const REPLAY_STAMP_KEY = "sentry_replay_sent_at"
const REPLAY_PAGES = ["/appointments/new", "/users/sign_in"]

// Купон и телефон в адресе визарда, токен и код входа; `/c/<токен>` — пароль страницы сертификата
// (маскировка та же, что CertificateTokenScrub на сервере).
const SECRET_PARAMS = ["coupon", "token", "code", "phone"]
const QUERY_PAIR = /([?&])([^=&#\s?"']+)=([^&#\s"']*)/g
const CERTIFICATE_PATH = /\/c\/[^/?#\s"']+/g
const TELEGRAM_INIT_DATA = /#tgWebAppData=[^\s"']*/g
// Свой список поверх isBotUserAgent: тот не знает безголовый Chrome, Lighthouse и робота Метрики.
const ROBOT_AGENT = /HeadlessChrome|Lighthouse|YandexMetrika|bot[/;)]|crawler|spider/i

const meta = (name) => document.querySelector(`meta[name="${name}"]`)?.content

// Имя сверяется раскодированным: `?%63oupon=` и `?coupon[]=` Rails читает как coupon.
function isSecretParam(rawName) {
  let name = rawName
  try { name = decodeURIComponent(rawName.replace(/\+/g, " ")) } catch { /* битая кодировка — как есть */ }
  return SECRET_PARAMS.includes(name.toLowerCase().split("[")[0])
}

const hasSecretParam = (url) => [...url.matchAll(QUERY_PAIR)].some(([, , name]) => isSecretParam(name))

function scrub(value) {
  if (typeof value !== "string") return value
  return value
    .replace(QUERY_PAIR, (pair, separator, name) => (isSecretParam(name) ? `${separator}${name}=[FILTERED]` : pair))
    .replace(CERTIFICATE_PATH, "/c/[FILTERED]")
    .replace(TELEGRAM_INIT_DATA, "#[FILTERED]")
}

// У адреса фрагмент срезается целиком: мини-приложение Telegram открывается с `#tgWebAppData=…`, а это
// имя посетителя и подпись, по которой /telegram/login час пускает в его кабинет. Сайту фрагмент не нужен.
function scrubUrl(value) {
  return typeof value === "string" ? scrub(value.replace(/#.*$/s, "")) : value
}

function scrubBreadcrumb(breadcrumb) {
  breadcrumb.message = scrub(breadcrumb.message)
  const data = breadcrumb.data
  if (data) ["url", "from", "to"].forEach((key) => { data[key] = scrubUrl(data[key]) })
  return breadcrumb
}

// Один проход на событие любого типа: ошибка, её крошки и реплей (`urls`) идут разными путями,
// а процессор событий видят все.
function scrubEvent(event) {
  const request = event.request
  if (request) {
    request.url = scrubUrl(request.url)
    if (request.headers?.Referer) request.headers.Referer = scrubUrl(request.headers.Referer)
  }
  event.message = scrub(event.message)
  // Кадр встроенного скрипта несёт в имени файла адрес страницы.
  event.exception?.values?.forEach((exception) => {
    exception.value = scrub(exception.value)
    exception.stacktrace?.frames?.forEach((frame) => { frame.filename = scrubUrl(frame.filename) })
  })
  event.breadcrumbs?.forEach(scrubBreadcrumb)
  if (event.urls) event.urls = event.urls.map(scrubUrl)
  return event
}

let eventsSent = 0

// Страница без счётчиков (вход в админку) могла прийти Turbo-переходом с уже загруженным SDK.
function beforeSend(event) {
  if (document.body?.hasAttribute("data-skip-analytics")) return null
  if (eventsSent >= EVENTS_PER_PAGE_LOAD) return null
  eventsSent += 1
  return event
}

// То же правило, что allowed() в analytics/_lazy_tags и trackingAllowed в cookie_consent_controller.js:
// реплей включается только там, где грузятся Метрика и GA.
function trackingAllowed() {
  const choice = document.cookie.match(/(?:^|; )cookie_consent=(yes|no)(?:;|$)/)?.[1]
  return meta("sentry-consent-opt-in") === "true" ? choice === "yes" : choice !== "no"
}

// Отметку ставим до отправки: закрытое хранилище (приватный режим) значит «реплея нет», а не
// «реплей на каждую ошибку».
function claimReplaySlot() {
  try {
    const sentAt = Number(window.localStorage.getItem(REPLAY_STAMP_KEY))
    if (sentAt && Date.now() - sentAt < REPLAY_EVERY_MS) return false
    window.localStorage.setItem(REPLAY_STAMP_KEY, String(Date.now()))
    return true
  } catch {
    return false
  }
}

// Адрес целиком реплей пишет мимо любых колбэков (снимок страницы), поэтому с секретом в адресе
// он не записывается вовсе.
const replayWanted = () =>
  Boolean(meta("sentry-replay-src")) && REPLAY_PAGES.includes(window.location.pathname) &&
  !hasSecretParam(window.location.search) &&
  trackingAllowed() && !document.body?.hasAttribute("data-skip-analytics")

let replay = null
let replayLoading = false

async function loadReplay(client) {
  replayLoading = true
  // Реплей — отдельный бандл: 56 КБ gzip нужны двум страницам из всего сайта.
  const { replayIntegration } = await import(meta("sentry-replay-src"))
  replay = replayIntegration({
    maskAllText: true,
    maskAllInputs: true,
    blockAllMedia: true,
    // Без восстановления из sessionStorage: начатая после ошибки запись иначе продолжилась бы на
    // следующей полной загрузке мимо проверки согласия и суточного предохранителя.
    stickySession: false,
    // Атрибуты maskAllText не трогает, а номер сертификата из «Записаться по сертификату» визард
    // держит в data-атрибуте. Первые три — умолчание Sentry, список заменяется целиком.
    maskAttributes: ["title", "placeholder", "aria-label", "data-booking--wizard-initial-coupon-value"],
    beforeErrorSampling: () => replayWanted() && claimReplaySlot(),
    beforeAddRecordingEvent: (recordingEvent) => {
      const payload = recordingEvent.data?.payload
      if (payload) {
        payload.description = scrubUrl(payload.description)
        if (payload.data) payload.data.url = scrubUrl(payload.data.url)
      }
      return recordingEvent
    }
  })
  client.addIntegration(replay)
}

function stopReplay() {
  if (replay?.getReplayId()) replay.stop()
}

// Запись в буфер — только на страницах записи и входа: ушёл посетитель на другую страницу Turbo —
// буфер останавливаем, вернулся — начинаем заново.
function syncReplay(client) {
  if (!replayWanted()) {
    stopReplay()
    return
  }
  if (replay) {
    if (!replay.getReplayId()) replay.startBuffering()
  } else if (!replayLoading) {
    loadReplay(client).catch(() => { replayLoading = false })
  }
}

function start() {
  const dsn = meta("sentry-dsn")
  if (!dsn || isBotUserAgent() || ROBOT_AGENT.test(navigator.userAgent) || navigator.webdriver) return

  const client = new BrowserClient({
    dsn,
    release: meta("sentry-release"),
    environment: "production",
    sendDefaultPii: false,
    sampleRate: 1.0,
    tracesSampleRate: 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 1.0,
    transport: makeFetchTransport,
    stackParser: defaultStackParser,
    ignoreErrors: [
      /ResizeObserver loop/,
      /Non-Error promise rejection captured/,
      // Оборванный или заблокированный fetch в Safari, Chrome и Firefox: причину по событию не отличить.
      // Якоря — чтобы не спрятать «Failed to fetch dynamically imported module»; хост в скобках дописывает SDK.
      /^Load failed( \(.+\))?$/,
      /^Failed to fetch( \(.+\))?$/,
      /^NetworkError when attempting to fetch resource\.?( \(.+\))?$/
    ],
    denyUrls: [
      /^(chrome|moz|safari(-web)?)-extension:\/\//,
      /mc\.yandex\.(ru|by|com)/,
      /yastatic\.net/,
      /googletagmanager\.com/,
      /google-analytics\.com/
    ],
    beforeSend,
    beforeBreadcrumb: scrubBreadcrumb,
    integrations: [
      eventFiltersIntegration(),
      functionToStringIntegration(),
      browserApiErrorsIntegration(),
      // Консоль не пишем: туда попадают ответы API визарда с телефоном.
      breadcrumbsIntegration({ console: false }),
      globalHandlersIntegration(),
      linkedErrorsIntegration(),
      dedupeIntegration(),
      httpContextIntegration()
    ]
  })
  getCurrentScope().setClient(client)
  client.init()
  client.addEventProcessor(scrubEvent)

  syncReplay(client)
  // Останавливаем до перехода: после ошибки запись идёт уже не в буфер, и stop() на turbo:load
  // успел бы отправить первый кадр следующей страницы.
  document.addEventListener("turbo:before-visit", stopReplay)
  document.addEventListener("turbo:load", () => syncReplay(client))
}

start()
